import classNames from 'classnames';
import { m } from 'framer-motion';
import { useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import CtaLinkList, {
  CtaLinkListProps,
} from '@/components/organisms/cta-link-list/cta-link-list';
import styles from './accordion.module.scss';

export interface AccordionProps {
  /** The class name to apply */
  className?: string;
  /** The list of accordion items to show */
  items?: {
    ctaLinks?: CtaLinkListProps['items'];
    headline?: string;
    text?: string;
  }[];
}

const Accordion = ({ className, items = [] }: AccordionProps) => {
  const [activeAccordions, setActiveAccordions] = useState<number[]>([]);

  return (
    <div className={classNames(styles.component, className)}>
      {items.map((item, itemIndex) => (
        <div
          key={itemIndex}
          className={classNames(styles.item, {
            [styles.isActive]: activeAccordions.includes(itemIndex),
          })}
        >
          <button
            className={styles.itemHeader}
            onClick={() =>
              setActiveAccordions(previousValue =>
                previousValue.includes(itemIndex)
                  ? [...previousValue.filter(value => value !== itemIndex)]
                  : [...previousValue, itemIndex],
              )
            }
          >
            {item.headline && (
              <Typography
                className={styles.itemHeaderHeadline}
                tag="h3"
                tagStyle="bodyLargeBold"
              >
                {item.headline}
              </Typography>
            )}

            <Icon
              className={styles.itemHeaderIcon}
              name="ChevronDown"
              size={20}
            />
          </button>

          <m.div
            animate={{
              height: activeAccordions.includes(itemIndex) ? 'auto' : 0,
            }}
            className={styles.itemFooter}
            initial={false}
          >
            <div className={styles.itemFooterContent}>
              {item.text && <RichText text={item.text} />}

              {item.ctaLinks && item.ctaLinks.length > 0 && (
                <CtaLinkList items={item.ctaLinks} />
              )}
            </div>
          </m.div>
        </div>
      ))}
    </div>
  );
};

Accordion.displayName = 'Accordion';

export default Accordion;
